/**
 * Service for managing agenda/calendar
 * Communicates with the ColdFusion REST API
 */


const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/agenda`;

/**
 * En-têtes d'un appel qui écrit.
 *
 * Le serveur vérifie désormais que l'événement/l'entrée visée appartient
 * bien à l'appelant (ou, pour une réponse à invitation, que l'appelant EST
 * le participant concerné). Il lui faut donc une identité prouvée : le JWT
 * signé au login, et non le `peopleId`/`createdById` que le corps de la
 * requête déclarait jusqu'ici — ce dernier venait du client, et se
 * remplaçait.
 *
 * Le jeton est écrit sous `authToken` par `AuthContext.saveUserToStorage` ;
 * `sessionStorage` prend le relais quand l'utilisateur n'a pas coché « rester
 * connecté ».
 */
function enTetesEcriture(avecCorps = true) {
    let token = null;
    try {
        token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    } catch {
        token = null;
    }

    const headers = {};
    if (avecCorps) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
}

/**
 * Helper function to handle API responses
 */
const handleResponse = async (response) => {
    const responseText = await response.text();

    let result;
    try {
        result = JSON.parse(responseText);
    } catch (parseError) {
        console.error('Failed to parse JSON:', parseError);
        console.error('Raw response:', responseText);
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 200)}`);
    }

    if (!response.ok) {
        const error = new Error(result.message || result.MESSAGE || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = result;
        throw error;
    }

    return result;
};

// ========= AVAILABILITY TEMPLATES =========

/**
 * Get availability templates for a user
 * @param {number} peopleId - User's ID
 * @returns {Promise<Array>} Array of availability templates
 */
export const getAvailabilityTemplates = async (peopleId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/availability/${peopleId}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || [];

        return data.map(t => ({
            id: t.id || t.ID,
            peopleId: t.peopleId || t.peopleid || t.PEOPLEID,
            dayOfWeek: t.dayOfWeek || t.dayofweek || t.DAYOFWEEK,
            startTime: t.startTime || t.starttime || t.STARTTIME,
            endTime: t.endTime || t.endtime || t.ENDTIME,
            isActive: t.isActive || t.isactive || t.ISACTIVE
        }));
    } catch (error) {
        console.error('Error getting availability templates:', error);
        return [];
    }
};

/**
 * Save availability templates for a user (replaces all existing)
 * @param {number} peopleId - User's ID
 * @param {Array} slots - Array of { dayOfWeek, startTime, endTime }
 * @returns {Promise<Object>} Result
 */
export const saveAvailabilityTemplates = async (peopleId, slots) => {
    try {
        const response = await fetch(`${API_BASE_URL}/availability`, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify({ peopleId, slots })
        });

        return await handleResponse(response);
    } catch (error) {
        console.error('Error saving availability templates:', error);
        throw error;
    }
};

// ========= CALENDAR EVENTS =========

/**
 * Get events for a user
 * @param {number} peopleId - User's ID
 * @param {Object} options - Filter options
 * @returns {Promise<Array>} Array of events
 */
export const getEvents = async (peopleId, options = {}) => {
    try {
        const params = new URLSearchParams();
        if (options.startDate) params.append('startDate', options.startDate);
        if (options.endDate) params.append('endDate', options.endDate);
        if (options.eventType) params.append('eventType', options.eventType);

        const response = await fetch(`${API_BASE_URL}/events/${peopleId}?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || [];

        return data.map(e => ({
            id: e.id || e.ID,
            eventUuid: e.eventUuid || e.eventuuid || e.EVENTUUID,
            createdById: e.createdById || e.createdbyid || e.CREATEDBYID,
            title: e.title || e.TITLE || '',
            description: e.description || e.DESCRIPTION || '',
            placeName: e.placeName || e.placename || e.PLACENAME || '',
            placeAddress: e.placeAddress || e.placeaddress || e.PLACEADDRESS || '',
            latitude: e.latitude || e.LATITUDE || null,
            longitude: e.longitude || e.LONGITUDE || null,
            start: e.startDateTime || e.startdate || e.startDate || e.STARTDATE,
            end: e.endDateTime || e.enddate || e.endDate || e.ENDDATE,
            allDay: e.allDay || e.allday || e.ALLDAY || false,
            eventType: e.eventType || e.eventtype || e.EVENTTYPE || 'event',
            status: e.status || e.STATUS || 'active',
            color: e.color || e.COLOR || getEventColor(e.eventType || e.eventtype || e.EVENTTYPE),
            isPrivate: e.isPrivate || e.isprivate || e.ISPRIVATE || false,
            recurring: e.recurring || e.RECURRING || false,
            recurrenceRule: e.recurrenceRule || e.recurrencerule || e.RECURRENCERULE || '',
            maxParticipants: e.maxParticipants || e.maxparticipants || e.MAXPARTICIPANTS || 0,
            participantCount: e.participantCount || e.participantcount || e.PARTICIPANTCOUNT || 0,
            reminderMinutes: e.reminderMinutes || e.reminderminutes || e.REMINDERMINUTES || 15
        }));
    } catch (error) {
        console.error('Error getting events:', error);
        return [];
    }
};

/**
 * Get default color for event type
 */
const getEventColor = (eventType) => {
    const colors = {
        event: '#3b82f6',        // blue
        appointment: '#8b5cf6', // purple
        meeting: '#f59e0b',     // amber
        availability: '#22c55e' // green
    };
    return colors[eventType] || '#6b7280';
};

/**
 * Create a new event
 * @param {Object} eventData - Event data
 * @returns {Promise<Object>} Created event
 */
/**
 * Format datetime for ColdFusion (needs full ISO format with seconds)
 */
const formatDateTimeForCF = (dateTime) => {
    if (!dateTime) return null;
    const s = String(dateTime).trim();
    // Already full datetime with seconds
    if (s.length >= 19) return s.slice(0, 19);
    // HH:MM only (length 16: "2025-12-10T06:00")
    if (s.length === 16) return s + ':00';
    // Date only (length 10: "2025-12-10") — assume midnight
    if (s.length === 10) return s + 'T00:00:00';
    return s;
};

/**
 * Convert recurrence selection to RRULE format
 */
const buildRecurrenceRule = (recurrence, endDate) => {
    if (!recurrence || recurrence === 'none') return '';

    let rule = '';
    switch (recurrence) {
        case 'daily':
            rule = 'FREQ=DAILY';
            break;
        case 'weekly':
            rule = 'FREQ=WEEKLY';
            break;
        case 'biweekly':
            rule = 'FREQ=WEEKLY;INTERVAL=2';
            break;
        case 'monthly':
            rule = 'FREQ=MONTHLY';
            break;
        case 'yearly':
            rule = 'FREQ=YEARLY';
            break;
        case 'weekdays':
            rule = 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR';
            break;
        default:
            return '';
    }

    if (endDate) {
        // Format date as YYYYMMDD for RRULE
        const dateStr = endDate.replace(/-/g, '');
        rule += `;UNTIL=${dateStr}`;
    }

    return rule;
};

export const createEvent = async (eventData) => {
    try {
        const startDT = formatDateTimeForCF(eventData.start || eventData.startDateTime);
        const endDT = formatDateTimeForCF(eventData.end || eventData.endDateTime);
        const isRecurring = eventData.recurrence && eventData.recurrence !== 'none';
        const recurrenceRule = buildRecurrenceRule(eventData.recurrence, eventData.recurrenceEndDate);

        const response = await fetch(`${API_BASE_URL}/events`, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify({
                createdById: eventData.createdById || eventData.peopleId,
                title: eventData.title,
                description: eventData.description || '',
                placeName: eventData.placeName || eventData.location || '',
                placeAddress: eventData.placeAddress || '',
                latitude: eventData.latitude || null,
                longitude: eventData.longitude || null,
                startDate: startDT,
                endDate: endDT,
                allDay: eventData.allDay || false,
                eventType: eventData.eventType || 'event',
                status: eventData.status || 'active',
                color: eventData.color || '',
                isPrivate: eventData.isPrivate || false,
                recurring: isRecurring,
                recurrenceRule: recurrenceRule,
                maxParticipants: eventData.maxParticipants || 0,
                reminderMinutes: eventData.reminderMinutes || 15
            })
        });

        const result = await handleResponse(response);
        return result.data || result.DATA;
    } catch (error) {
        console.error('Error creating event:', error);
        throw error;
    }
};

/**
 * Update an event
 * @param {string} eventUuid - Event UUID
 * @param {Object} eventData - Updated event data
 * @returns {Promise<Object>} Result
 */
export const updateEvent = async (eventUuid, eventData) => {
    try {
        const startDT = formatDateTimeForCF(eventData.start || eventData.startDateTime);
        const endDT = formatDateTimeForCF(eventData.end || eventData.endDateTime);
        const isRecurring = eventData.recurrence && eventData.recurrence !== 'none';
        const recurrenceRule = buildRecurrenceRule(eventData.recurrence, eventData.recurrenceEndDate);

        const response = await fetch(`${API_BASE_URL}/events/${eventUuid}`, {
            method: 'PUT',
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify({
                title: eventData.title,
                description: eventData.description || '',
                placeName: eventData.placeName || eventData.location || '',
                placeAddress: eventData.placeAddress || '',
                latitude: eventData.latitude || null,
                longitude: eventData.longitude || null,
                startDate: startDT,
                endDate: endDT,
                allDay: eventData.allDay || false,
                eventType: eventData.eventType || 'event',
                status: eventData.status || 'active',
                color: eventData.color || '',
                isPrivate: eventData.isPrivate || false,
                recurring: isRecurring,
                recurrenceRule: recurrenceRule,
                maxParticipants: eventData.maxParticipants || 0,
                reminderMinutes: eventData.reminderMinutes || 15
            })
        });

        return await handleResponse(response);
    } catch (error) {
        console.error('Error updating event:', error);
        throw error;
    }
};

/**
 * Delete an event
 * @param {string} eventUuid - Event UUID
 * @returns {Promise<boolean>} Success
 */
export const deleteEvent = async (eventUuid) => {
    try {
        const response = await fetch(`${API_BASE_URL}/events/${eventUuid}`, {
            method: 'DELETE',
            credentials: 'include',
            headers: enTetesEcriture(false)
        });

        await handleResponse(response);
        return true;
    } catch (error) {
        console.error('Error deleting event:', error);
        return false;
    }
};

// ========= PUBLIC AVAILABILITY =========

/**
 * Get public availability for a person (no auth required)
 * @param {string} peopleUuid - Person's UUID
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} Availability data
 */
export const getPublicAvailability = async (peopleUuid, startDate, endDate) => {
    try {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await fetch(`${API_BASE_URL}/public/${peopleUuid}?${params}`, {
            method: 'GET'
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || {};

        return {
            templates: (data.templates || data.TEMPLATES || []).map(t => ({
                dayOfWeek: t.dayOfWeek || t.dayofweek || t.DAYOFWEEK,
                startTime: t.startTime || t.starttime || t.STARTTIME,
                endTime: t.endTime || t.endtime || t.ENDTIME
            })),
            events: (data.events || data.EVENTS || []).map(e => ({
                startDateTime: e.startDateTime || e.startdatetime || e.STARTDATETIME,
                endDateTime: e.endDateTime || e.enddatetime || e.ENDDATETIME,
                allDay: e.allDay || e.allday || e.ALLDAY || false
            })),
            exceptions: (data.exceptions || data.EXCEPTIONS || []).map(ex => ({
                date: ex.date || ex.DATE || '',
                reason: ex.reason || ex.REASON || '',
                isFullDay: ex.isFullDay || ex.isfullday || ex.ISFULLDAY || true,
                startTime: ex.startTime || ex.starttime || ex.STARTTIME || '',
                endTime: ex.endTime || ex.endtime || ex.ENDTIME || ''
            }))
        };
    } catch (error) {
        console.error('Error getting public availability:', error);
        return { templates: [], events: [], exceptions: [] };
    }
};

// ========= AVAILABILITY EXCEPTIONS =========

/**
 * Add an availability exception
 * @param {Object} exceptionData - Exception data
 * @returns {Promise<Object>} Result
 */
export const addException = async (exceptionData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/exceptions`, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify({
                peopleId: exceptionData.peopleId,
                exceptionDate: exceptionData.date || exceptionData.exceptionDate,
                reason: exceptionData.reason || '',
                isFullDay: exceptionData.isFullDay !== false,
                startTime: exceptionData.startTime || '',
                endTime: exceptionData.endTime || ''
            })
        });

        return await handleResponse(response);
    } catch (error) {
        console.error('Error adding exception:', error);
        throw error;
    }
};

/**
 * Delete an availability exception
 * @param {number} exceptionId - Exception ID
 * @returns {Promise<boolean>} Success
 */
export const deleteException = async (exceptionId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/exceptions/${exceptionId}`, {
            method: 'DELETE',
            credentials: 'include',
            headers: enTetesEcriture(false)
        });

        await handleResponse(response);
        return true;
    } catch (error) {
        console.error('Error deleting exception:', error);
        return false;
    }
};

// ========= GENERATE AVAILABILITY EVENTS =========

/**
 * Generate availability events from templates for a date range
 * @param {number} peopleId - User's ID
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @returns {Promise<Object>} Result with count of created events
 */
export const generateAvailabilityEvents = async (peopleId, startDate, endDate) => {
    try {
        const response = await fetch(`${API_BASE_URL}/generate`, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify({ peopleId, startDate, endDate })
        });

        const result = await handleResponse(response);
        return result.data || result.DATA || { created: 0 };
    } catch (error) {
        console.error('Error generating availability events:', error);
        throw error;
    }
};

// ========= HELPER FUNCTIONS =========

/**
 * Get available time slots for a date based on templates and existing events
 * @param {Array} templates - Availability templates
 * @param {Array} events - Existing events
 * @param {Date} date - The date to check
 * @returns {Array} Available time slots
 */
export const getAvailableSlots = (templates, events, date) => {
    const dayOfWeek = date.getDay(); // 0 = Sunday
    const dateStr = date.toISOString().split('T')[0];

    // Get templates for this day
    const dayTemplates = templates.filter(t => t.dayOfWeek === dayOfWeek);

    if (dayTemplates.length === 0) return [];

    // Get events for this day
    const dayEvents = events.filter(e => {
        const eventDate = new Date(e.start || e.startDateTime).toISOString().split('T')[0];
        return eventDate === dateStr;
    });

    // Calculate available slots by subtracting events from templates
    const availableSlots = [];

    dayTemplates.forEach(template => {
        const templateStart = parseTime(template.startTime);
        const templateEnd = parseTime(template.endTime);

        let slots = [{ start: templateStart, end: templateEnd }];

        // Subtract each event from the slots
        dayEvents.forEach(event => {
            const eventStart = new Date(event.start || event.startDateTime);
            const eventEnd = new Date(event.end || event.endDateTime);
            const eventStartMinutes = eventStart.getHours() * 60 + eventStart.getMinutes();
            const eventEndMinutes = eventEnd.getHours() * 60 + eventEnd.getMinutes();

            slots = slots.flatMap(slot => {
                if (eventEndMinutes <= slot.start || eventStartMinutes >= slot.end) {
                    // No overlap
                    return [slot];
                } else if (eventStartMinutes <= slot.start && eventEndMinutes >= slot.end) {
                    // Event covers entire slot
                    return [];
                } else if (eventStartMinutes > slot.start && eventEndMinutes < slot.end) {
                    // Event is in the middle
                    return [
                        { start: slot.start, end: eventStartMinutes },
                        { start: eventEndMinutes, end: slot.end }
                    ];
                } else if (eventStartMinutes <= slot.start) {
                    // Event overlaps start
                    return [{ start: eventEndMinutes, end: slot.end }];
                } else {
                    // Event overlaps end
                    return [{ start: slot.start, end: eventStartMinutes }];
                }
            });
        });

        availableSlots.push(...slots.map(s => ({
            startTime: formatMinutes(s.start),
            endTime: formatMinutes(s.end)
        })));
    });

    return availableSlots;
};

/**
 * Parse time string to minutes
 */
const parseTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
};

/**
 * Format minutes to time string
 */
const formatMinutes = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

/**
 * Days of week labels
 */
export const DAYS_OF_WEEK = [
    { value: 0, key: 'sunday' },
    { value: 1, key: 'monday' },
    { value: 2, key: 'tuesday' },
    { value: 3, key: 'wednesday' },
    { value: 4, key: 'thursday' },
    { value: 5, key: 'friday' },
    { value: 6, key: 'saturday' }
];

/**
 * Event type labels
 */
export const EVENT_TYPES = [
    { value: 'event', color: '#3b82f6' },
    { value: 'appointment', color: '#8b5cf6' },
    { value: 'meeting', color: '#f59e0b' },
    { value: 'availability', color: '#22c55e' }
];

/**
 * Recurrence options
 */
export const RECURRENCE_OPTIONS = [
    { value: 'none' },
    { value: 'daily' },
    { value: 'weekly' },
    { value: 'biweekly' },
    { value: 'monthly' },
    { value: 'yearly' },
    { value: 'weekdays' }
];

/**
 * Parse RRULE to simple recurrence type
 */
export const parseRecurrenceRule = (rule) => {
    if (!rule) return 'none';

    if (rule.includes('BYDAY=MO,TU,WE,TH,FR')) return 'weekdays';
    if (rule.includes('INTERVAL=2') && rule.includes('FREQ=WEEKLY')) return 'biweekly';
    if (rule.includes('FREQ=DAILY')) return 'daily';
    if (rule.includes('FREQ=WEEKLY')) return 'weekly';
    if (rule.includes('FREQ=MONTHLY')) return 'monthly';
    if (rule.includes('FREQ=YEARLY')) return 'yearly';

    return 'none';
};

/**
 * Get recurrence end date from RRULE
 */
export const getRecurrenceEndDate = (rule) => {
    if (!rule) return '';

    const match = rule.match(/UNTIL=(\d{8})/);
    if (match) {
        const dateStr = match[1];
        return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
    }

    return '';
};

// ========= AGENDA PARTICIPANTS =========
// Gestion des participants aux événements agenda

/**
 * Get participants for an agenda event
 * @param {number} agendaId - Agenda event ID
 * @returns {Promise<Array>} Array of participants
 */
export const getAgendaParticipants = async (agendaId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/participants/${agendaId}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || [];

        return data.map(p => ({
            id: p.id || p.ID,
            agendaId: p.agendaId || p.AGENDAID,
            peopleId: p.peopleId || p.PEOPLEID,
            status: p.status || p.STATUS || 'pending',
            invitedAt: p.invitedAt || p.INVITEDAT,
            respondedAt: p.respondedAt || p.RESPONDEDAT,
            firstName: p.firstName || p.FIRSTNAME || '',
            lastName: p.lastName || p.LASTNAME || '',
            email: p.email || p.EMAIL || '',
            photoUrl: p.photoUrl || p.PHOTOURL || ''
        }));
    } catch (error) {
        console.error('Error getting agenda participants:', error);
        return [];
    }
};

/**
 * Add a participant to an agenda event
 * @param {number} agendaId - Agenda event ID
 * @param {number} peopleId - Participant's people ID
 * @param {string} status - Initial status: 'pending', 'confirmed', 'declined'
 * @returns {Promise<Object>} Result with participantId
 */
export const addAgendaParticipant = async (agendaId, peopleId, status = 'pending') => {
    try {
        const response = await fetch(`${API_BASE_URL}/participants`, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify({ agendaId, peopleId, status })
        });

        const result = await handleResponse(response);
        return {
            success: result.success || result.SUCCESS,
            participantId: result.data?.participantId || result.DATA?.PARTICIPANTID,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error adding agenda participant:', error);
        throw error;
    }
};

/**
 * Update participant status
 * @param {number} agendaId - Agenda event ID
 * @param {number} peopleId - Participant's people ID
 * @param {string} status - New status: 'pending', 'confirmed', 'declined'
 * @returns {Promise<Object>} Result
 */
export const updateParticipantStatus = async (agendaId, peopleId, status) => {
    try {
        const response = await fetch(`${API_BASE_URL}/participants/${agendaId}/${peopleId}`, {
            method: 'PUT',
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify({ status })
        });

        const result = await handleResponse(response);
        return {
            success: result.success || result.SUCCESS,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error updating participant status:', error);
        throw error;
    }
};

/**
 * Remove a participant from an agenda event
 * @param {number} agendaId - Agenda event ID
 * @param {number} peopleId - Participant's people ID
 * @returns {Promise<Object>} Result
 */
export const removeAgendaParticipant = async (agendaId, peopleId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/participants/${agendaId}/${peopleId}`, {
            method: 'DELETE',
            credentials: 'include',
            headers: enTetesEcriture(false)
        });

        const result = await handleResponse(response);
        return {
            success: result.success || result.SUCCESS,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error removing agenda participant:', error);
        throw error;
    }
};

// ========= AGENDA GROUPS =========

/**
 * Get groups linked to an agenda event
 * @param {number} agendaId - Agenda event ID
 * @returns {Promise<Array>} List of groups
 */
export const getAgendaGroups = async (agendaId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/groups/${agendaId}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || [];

        return data.map(g => ({
            id: g.id || g.ID,
            agendaId: g.agendaId || g.AGENDAID,
            groupId: g.groupId || g.GROUPID,
            groupName: g.groupName || g.GROUPNAME,
            groupUuid: g.groupUuid || g.GROUPUUID,
            memberCount: g.memberCount || g.MEMBERCOUNT || 0,
            createdAt: g.createdAt || g.CREATEDAT
        }));
    } catch (error) {
        console.error('Error getting agenda groups:', error);
        return [];
    }
};

/**
 * Add a group to an agenda event (also adds all group members as participants)
 * @param {number} agendaId - Agenda event ID
 * @param {number} groupId - Group ID to add
 * @returns {Promise<Object>} Result with id and membersAdded
 */
export const addAgendaGroup = async (agendaId, groupId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/groups`, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify({ agendaId, groupId })
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || {};
        return {
            success: result.success || result.SUCCESS,
            id: data.id || data.ID,
            membersAdded: data.membersAdded || data.MEMBERSADDED || 0,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error adding agenda group:', error);
        throw error;
    }
};

/**
 * Remove a group from an agenda event
 * @param {number} agendaId - Agenda event ID
 * @param {number} groupId - Group ID to remove
 * @returns {Promise<Object>} Result
 */
export const removeAgendaGroup = async (agendaId, groupId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/groups/${agendaId}/${groupId}`, {
            method: 'DELETE',
            credentials: 'include',
            headers: enTetesEcriture(false)
        });

        const result = await handleResponse(response);
        return {
            success: result.success || result.SUCCESS,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error removing agenda group:', error);
        throw error;
    }
};

/**
 * Check if a user is participating in an agenda event
 * @param {number} agendaId - Agenda event ID
 * @param {number} peopleId - User's people ID
 * @returns {Promise<Object>} Result with isParticipating and status
 */
export const checkParticipant = async (agendaId, peopleId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/check-participant/${agendaId}/${peopleId}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || {};
        return {
            isParticipating: data.isParticipating || data.ISPARTICIPATING || false,
            status: data.status || data.STATUS || ''
        };
    } catch (error) {
        console.error('Error checking participant:', error);
        return { isParticipating: false, status: '' };
    }
};

// ========= EVENT-AGENDA LINKING =========
// Ces fonctions permettent de lier/délier un event public à l'agenda personnel

/**
 * Link a public event to the user's personal agenda
 * Creates an agenda entry and tracks the link
 * @param {number} eventId - Public event ID
 * @param {number} peopleId - User's ID
 * @param {string} linkType - Type of link: 'creator' or 'participant'
 * @returns {Promise<Object>} Result with linkId and agendaId
 */
export const linkEventToAgenda = async (eventId, peopleId, linkType = 'participant') => {
    try {
        const response = await fetch(`${API_BASE_URL}/link-event`, {
            method: 'POST',
            credentials: 'include',
            headers: enTetesEcriture(),
            body: JSON.stringify({ eventId, peopleId, linkType })
        });

        const result = await handleResponse(response);
        return {
            success: result.success || result.SUCCESS,
            linkId: result.data?.linkId || result.DATA?.LINKID,
            agendaId: result.data?.agendaId || result.DATA?.AGENDAID,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error linking event to agenda:', error);
        throw error;
    }
};

/**
 * Unlink a public event from the user's personal agenda
 * Removes the agenda entry and the link
 * @param {number} eventId - Public event ID
 * @param {number} peopleId - User's ID
 * @returns {Promise<Object>} Result
 */
export const unlinkEventFromAgenda = async (eventId, peopleId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/unlink-event/${eventId}/${peopleId}`, {
            method: 'DELETE',
            credentials: 'include',
            headers: enTetesEcriture(false)
        });

        const result = await handleResponse(response);
        return {
            success: result.success || result.SUCCESS,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error unlinking event from agenda:', error);
        throw error;
    }
};

/**
 * Check if a public event is linked to the user's agenda
 * @param {number} eventId - Public event ID
 * @param {number} peopleId - User's ID
 * @returns {Promise<Object>} Result with isLinked, linkId, agendaId
 */
export const isEventLinkedToAgenda = async (eventId, peopleId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/check-link/${eventId}/${peopleId}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || {};
        return {
            isLinked: data.isLinked || data.ISLINKED || false,
            linkId: data.linkId || data.LINKID || 0,
            agendaId: data.agendaId || data.AGENDAID || 0
        };
    } catch (error) {
        console.error('Error checking event link:', error);
        return { isLinked: false, linkId: 0, agendaId: 0 };
    }
};

/**
 * Get all public events linked to the user's agenda
 * @param {number} peopleId - User's ID
 * @returns {Promise<Array>} Array of linked events
 */
export const getLinkedEvents = async (peopleId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/linked-events/${peopleId}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || [];

        return data.map(link => ({
            linkId: link.linkId || link.LINKID,
            eventId: link.eventId || link.EVENTID,
            agendaId: link.agendaId || link.AGENDAID,
            linkType: link.linkType || link.LINKTYPE,
            linkedAt: link.linkedAt || link.LINKEDAT,
            eventUuid: link.eventUuid || link.EVENTUUID,
            agendaEventUuid: link.agendaEventUuid || link.AGENDAEVENTUUID,
            title: link.title || link.TITLE,
            description: link.description || link.DESCRIPTION || '',
            eventDate: link.eventDate || link.EVENTDATE,
            startTime: link.startTime || link.STARTTIME || '',
            endTime: link.endTime || link.ENDTIME || '',
            placeName: link.placeName || link.PLACENAME || '',
            placeAddress: link.placeAddress || link.PLACEADDRESS || '',
            country: link.country || link.COUNTRY || '',
            countryCode: link.countryCode || link.COUNTRYCODE || ''
        }));
    } catch (error) {
        console.error('Error getting linked events:', error);
        return [];
    }
};
